import { Link } from "react-router-dom";
import { Cpu, HeartHandshake, Sprout, Wrench } from "lucide-react";
import SEO from "../components/SEO";
import { Reveal, SectionTag } from "../components/bits";
import { AUTHOR } from "../data/business";

const PILLARS = [
  { icon: Cpu, title: "Serious hardware", text: "Genuine Samsung and Cree diodes driven by Meanwell — the same components trusted by commercial facilities, sized for home grow rooms." },
  { icon: Sprout, title: "Built for growers", text: "From a 56W starter COB to the 1000W StarShip, every fixture is spec'd around real canopy coverage and real power draw — no inflated wattage claims." },
  { icon: Wrench, title: "Designed in Australia", text: "Our range is designed and supported locally, backed by a 2-year Australian warranty and free express shipping AU-wide." },
  { icon: HeartHandshake, title: "Grower-first support", text: "Hanging distances, spectrum questions, room sizing — we answer like growers, not a call centre. Our grow guides are free for everyone." },
];

export default function About() {
  return (
    <div className="mx-auto max-w-7xl px-5 pb-28 pt-28 sm:px-8" data-testid="about-page">
      <SEO
        title="About SuperTechLED — Australian LED Grow Light Specialists"
        description="SuperTechLED designs high-performance LED grow lights in Australia: genuine Samsung & Cree diodes, Meanwell drivers, honest wattage and a 2-year local warranty."
      />
      <Reveal>
        <SectionTag>Our story</SectionTag>
        <h1 className="font-display mt-4 text-3xl font-bold tracking-tight text-[#F0FDF4] sm:text-4xl lg:text-5xl">
          Alien technology, grown in Australia
        </h1>
        <p className="mt-5 max-w-2xl text-sm leading-relaxed text-[#9FB8A8] sm:text-base">
          SuperTechLED started with a simple frustration: grow lights that promised the galaxy and delivered a
          glow stick. So we built a range around honest numbers — real power draw, real diode counts and real
          coverage footprints — then priced it for home growers, not just commercial facilities.
        </p>
        <p className="mt-4 max-w-2xl text-sm leading-relaxed text-[#9FB8A8] sm:text-base">
          Today the fleet spans quantum boards, bar lights and COB fixtures from 56W to 1000W, all running
          genuine Samsung and Cree diodes on Meanwell drivers, and all backed by free express shipping across
          Australia and a 2-year local warranty.
        </p>
      </Reveal>

      <div className="mt-16 grid gap-6 sm:grid-cols-2">
        {PILLARS.map((p, i) => (
          <Reveal key={p.title} delay={i * 0.08}>
            <div className="h-full rounded-3xl border border-[#39FF14]/15 bg-[#071009] p-8">
              <span className="flex h-11 w-11 items-center justify-center rounded-xl border border-[#39FF14]/25 bg-[#0A150F]">
                <p.icon className="h-5 w-5 text-[#39FF14]" />
              </span>
              <h2 className="font-display mt-5 text-lg font-bold text-[#F0FDF4]">{p.title}</h2>
              <p className="mt-2 text-sm leading-relaxed text-[#9FB8A8]">{p.text}</p>
            </div>
          </Reveal>
        ))}
      </div>

      <Reveal className="mt-16 rounded-3xl border border-[#39FF14]/15 bg-[#071009] p-8 sm:p-10" data-testid="about-founder">
        <div className="flex flex-col gap-6 sm:flex-row sm:items-start">
          <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full border border-[#39FF14]/25 bg-[#0A150F] font-display text-lg font-bold text-[#39FF14]">
            DM
          </div>
          <div>
            <SectionTag>Meet the founder</SectionTag>
            <h2 className="font-display mt-3 text-2xl font-bold tracking-tight text-[#F0FDF4] sm:text-3xl">{AUTHOR.name}</h2>
            {AUTHOR.bio.map((para) => (
              <p key={para.slice(0, 24)} className="mt-4 text-sm leading-relaxed text-[#9FB8A8] sm:text-base">
                {para}
              </p>
            ))}
          </div>
        </div>
      </Reveal>

      <Reveal className="mt-16 rounded-3xl border border-[#39FF14]/15 bg-[#071009] p-8 text-center sm:p-12">
        <h2 className="font-display text-2xl font-bold tracking-tight text-[#F0FDF4] sm:text-3xl">See what the fuss is about</h2>
        <p className="mx-auto mt-3 max-w-md text-sm text-[#9FB8A8]">Browse the full range, or start with our grow guides to dial in your room.</p>
        <div className="mt-7 flex flex-wrap items-center justify-center gap-4">
          <Link to="/shop" data-testid="about-shop-link" className="rounded-full bg-[#39FF14] px-7 py-3 font-display text-sm font-bold text-[#050B07] transition-all hover:shadow-[0_0_28px_rgba(57,255,20,0.4)]">
            Shop the range
          </Link>
          <Link to="/guides" data-testid="about-guides-link" className="rounded-full border border-[#39FF14]/30 px-7 py-3 font-display text-sm font-bold text-[#39FF14] transition-colors hover:border-[#39FF14]/70">
            Read the grow guides
          </Link>
        </div>
      </Reveal>
    </div>
  );
}
